import api from "../api";
import { CANCELLATION_API_ENDPOINTS } from "../../constants/jewellery.constant";

// Helper function to replace path params
const replacePathParams = (path, params) => {
  let result = path;
  Object.keys(params).forEach((key) => {
    result = result.replace(`:${key}`, params[key]);
  });
  return result;
};

const cancellationService = {
  // ==================== ELIGIBILITY & QUOTES ====================

  // Check cancellation eligibility
  checkEligibility: async (planId) => {
    const url = replacePathParams(
      CANCELLATION_API_ENDPOINTS.CHECK_CANCELLATION_ELIGIBILITY,
      { planId }
    );
    const response = await api.get(url);
    return response.data;
  },

  // Get cancellation quote
  getCancellationQuote: async (planId, reasonCategory) => {
    const url = replacePathParams(
      CANCELLATION_API_ENDPOINTS.GET_CANCELLATION_QUOTE,
      { planId }
    );
    const response = await api.get(url, {
      params: { reasonCategory },
    });
    return response.data;
  },

  // ==================== CANCELLATION PROCESSING ====================

  // Process cancellation
  processCancellation: async (planId, cancellationData) => {
    const url = replacePathParams(
      CANCELLATION_API_ENDPOINTS.PROCESS_CANCELLATION,
      { planId }
    );
    const response = await api.post(url, cancellationData);
    return response.data;
  },

  // Get cancellation status
  getCancellationStatus: async (planId) => {
    const url = replacePathParams(
      CANCELLATION_API_ENDPOINTS.GET_CANCELLATION_STATUS,
      { planId }
    );
    const response = await api.get(url);
    return response.data;
  },

  // ==================== APPROVAL WORKFLOW ====================

  // Get pending cancellations
  getPendingCancellations: async (filters = {}) => {
    const response = await api.get(
      CANCELLATION_API_ENDPOINTS.GET_PENDING_CANCELLATIONS,
      { params: filters }
    );
    return response.data;
  },

  // Approve cancellation request
  approveCancellationRequest: async (cancellationRequestId, approvalData) => {
    const url = replacePathParams(
      CANCELLATION_API_ENDPOINTS.APPROVE_CANCELLATION_REQUEST,
      { cancellationRequestId }
    );
    const response = await api.post(url, approvalData);
    return response.data;
  },

  // ==================== REFUND MANAGEMENT ====================

  // List all refunds
  listRefunds: async (filters = {}) => {
    const response = await api.get(CANCELLATION_API_ENDPOINTS.LIST_REFUNDS, {
      params: filters,
    });
    return response.data;
  },

  // Get specific refund status
  getRefundStatus: async (refundRequestId) => {
    const url = replacePathParams(
      CANCELLATION_API_ENDPOINTS.GET_REFUND_STATUS,
      {
        refundRequestId,
      }
    );
    const response = await api.get(url);
    return response.data;
  },

  // Update refund status
  updateRefundStatus: async (refundRequestId, statusData) => {
    const url = replacePathParams(
      CANCELLATION_API_ENDPOINTS.UPDATE_REFUND_STATUS,
      { refundRequestId }
    );
    const response = await api.patch(url, statusData);
    return response.data;
  },

  // Approve refund
  approveRefund: async (refundRequestId, approvalData) => {
    const url = replacePathParams(CANCELLATION_API_ENDPOINTS.APPROVE_REFUND, {
      refundRequestId,
    });
    const response = await api.post(url, approvalData);
    return response.data;
  },

  // Complete refund
  completeRefund: async (refundRequestId, completionData) => {
    const url = replacePathParams(CANCELLATION_API_ENDPOINTS.COMPLETE_REFUND, {
      refundRequestId,
    });
    const response = await api.post(url, completionData);
    return response.data;
  },

  // ==================== COLLECTION MANAGEMENT ====================

  // List all collections
  listCollections: async (filters = {}) => {
    const response = await api.get(
      CANCELLATION_API_ENDPOINTS.LIST_COLLECTIONS,
      {
        params: filters,
      }
    );
    return response.data;
  },

  // Get specific collection status
  getCollectionStatus: async (collectionRequestId) => {
    const url = replacePathParams(
      CANCELLATION_API_ENDPOINTS.GET_COLLECTION_STATUS,
      { collectionRequestId }
    );
    const response = await api.get(url);
    return response.data;
  },

  // Update collection status
  updateCollectionStatus: async (collectionRequestId, statusData) => {
    const url = replacePathParams(
      CANCELLATION_API_ENDPOINTS.UPDATE_COLLECTION_STATUS,
      { collectionRequestId }
    );
    const response = await api.patch(url, statusData);
    return response.data;
  },

  // Record collection payment
  recordCollectionPayment: async (collectionRequestId, paymentData) => {
    const url = replacePathParams(
      CANCELLATION_API_ENDPOINTS.RECORD_COLLECTION_PAYMENT,
      { collectionRequestId }
    );
    const response = await api.post(url, paymentData);
    return response.data;
  },

  // Waive collection (write-off)
  waiveCollection: async (collectionRequestId, waiveData) => {
    const url = replacePathParams(CANCELLATION_API_ENDPOINTS.WAIVE_COLLECTION, {
      collectionRequestId,
    });
    const response = await api.post(url, waiveData);
    return response.data;
  },

  // Resolve collection
  resolveCollection: async (collectionRequestId, resolutionData) => {
    const url = replacePathParams(
      CANCELLATION_API_ENDPOINTS.RESOLVE_COLLECTION,
      {
        collectionRequestId,
      }
    );
    const response = await api.post(url, resolutionData);
    return response.data;
  },

  // ==================== STATISTICS ====================

  // Get cancellation statistics
  getCancellationStats: async (filters = {}) => {
    const response = await api.get(
      CANCELLATION_API_ENDPOINTS.GET_CANCELLATION_STATS,
      { params: filters }
    );
    return response.data;
  },

  // Get refund summary
  getRefundStats: async (filters = {}) => {
    const response = await api.get(
      CANCELLATION_API_ENDPOINTS.GET_REFUND_STATS,
      {
        params: filters,
      }
    );
    return response.data;
  },

  // Get collection summary
  getCollectionStats: async (filters = {}) => {
    const response = await api.get(
      CANCELLATION_API_ENDPOINTS.GET_COLLECTION_STATS,
      { params: filters }
    );
    return response.data;
  },
};

export default cancellationService;
